// #region > Protect Badge (custom element boilerplate) - STATIC

/**
 * Self-contained custom element boilerplate. Loaded via its own <script> tag after protect.js -
 * it depends on SWAP.Language and SWAP.Protect.Elements.bind already existing on window.SWAP,
 * and on SWAP.Protect.State's 'protect' variable already being kept up to date elsewhere (see
 * SWAP.Protect.Product.protectReview in protect.js). No changes to protect.js are required: add
 * { "type": "swap-protect-badge" } to a group's `elements` array in the client profile and
 * SWAP.Protect.Elements.Build.element will create it like any other tag.
 *
 * The whole file is guarded by customElements.get so shared, once-only setup (the injected
 * <style> tag and the default language string) can never run twice, even if the file is
 * accidentally included more than once.
 */
if(!customElements.get('swap-protect-badge')) {

    // #region Shared CSS - injected once, styles the badge's own icon/label structure.

    if(!document.getElementById('swap-protect-badge-style')) {
        let style = document.createElement('style');
        style.id = 'swap-protect-badge-style';
        style.textContent = `
            .swap-protect-badge {
                display:flex;
                border-top: 1px solid #00000036;
                border-bottom: 1px solid #00000036;
                padding: 0.6rem;
                margin: 0.6rem 0;
                gap: 10px;
                font-size: 12px;
            }

            .swap-protect-badge__logo {
                aspect-ratio: 1 / 1;
                height: 100%;
            }

            .swap-protect-badge__logo svg {
                height: 100%;
                width: 100%;
            }

            .swap-protect-badge__leftcol {
                flex-grow:1;
            }

            .swap-protect-badge__title,
            .swap-protect-badge__rightcol {
                font-weight:bold;
            }

            .swap-protect-badge__rightcol {
                display: flex;
                flex-direction: column;
                justify-content: center;
                white-space: nowrap;
            }
        `;
        document.head.appendChild(style);
    }

    // #endregion

    // #region Shared default language strings - registered once, never overwrites a client override.

    if(SWAP.Language.languages.default === undefined) {
        SWAP.Language.new('default', {});
    }
    if(SWAP.Language.languages.default['swap_protect_badge_label'] === undefined) {
        SWAP.Language.languages.default['swap_protect_badge_label'] = 'Protect your order with Checkout+ for:';
    }
    if(SWAP.Language.languages.default['swap_protect_badge_cost'] === undefined) {
        SWAP.Language.languages.default['swap_protect_badge_cost'] = '{{ protect }}';
    }

    // #endregion

    /**
     * A small "Protect cost: {{ protect }}" badge, split into a static label span and a value
     * span bound separately, so only the value span re-renders when `protect` changes. `protect`
     * is the formatted protection fee already kept up to date by
     * SWAP.Protect.Product.protectReview (protect.js) via SWAP.Protect.State.set('protect', ...)
     * - the badge only binds to it, it never sets it.
     * @extends HTMLElement
     */
    class SwapProtectBadge extends HTMLElement {
        constructor() {
            super();
        }

        /**
         * Builds the badge's internal markup and binds its label/value spans to their reactive
         * language strings. Runs once per instance, the moment
         * SWAP.Protect.Elements.Build.elements appends it into a group's container.
         */
        connectedCallback() {
            this.innerHTML = `
                <div class="swap-protect-badge">
                    <div class="swap-protect-badge__logo">
                        <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect width="32" height="32" rx="16" fill="#1A1A1A"/>
                        <path d="M16.0007 6.01591C13.2459 7.8417 11.4747 8.46372 8.00074 9.01592C7.69249 20.8263 11.4514 25.984 16.0007 25.9841C20.1761 25.9841 24.309 20.8263 24.0007 9.01592C20.5268 8.46372 18.7555 7.8417 16.0007 6.01591Z" fill="#3C3C3C"/>
                        <path d="M13.1761 15.984L15.5296 18.1305L19.6761 13.484M8.00074 9.01592C11.4747 8.46372 13.2459 7.8417 16.0007 6.01591C18.7555 7.8417 20.5268 8.46372 24.0007 9.01592C24.309 20.8263 20.1761 25.9841 16.0007 25.9841C11.4514 25.984 7.69249 20.8263 8.00074 9.01592Z" stroke="white"/>
                        </svg>
                    </div>
                    <div class="swap-protect-badge__leftcol">
                        <div class="swap-protect-badge__title"></div>
                        <div class="swap-protect-badge__desc"></div>
                    </div>
                    <div class="swap-protect-badge__rightcol">
                        <span class="swap-protect-badge__value"></span>
                    </div>
                </div>                            
            `;

            SWAP.Protect.Elements.bind(
                this.querySelector('.swap-protect-badge__title'),
                'about_title'
            );

            SWAP.Protect.Elements.bind(
                this.querySelector('.swap-protect-badge__desc'),
                'about_content_default'
            );

            SWAP.Protect.Elements.bind(
                this.querySelector('.swap-protect-badge__value'),
                'swap_protect_badge_cost'
            );
        }
    }

    customElements.define('swap-protect-badge', SwapProtectBadge);
}

// #endregion
